import { useTranslation } from 'react-i18next';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { useAssistantTargetCollection } from '@/components/assistant/uiActions/useAssistantTargetCollection';
import { cn } from '@/lib/utils';
import {
  buildLocalizedWeekdayShortLabels,
  formatDateToDayOfMonthNumber,
  formatDateToFullWeekdayDate,
  formatDateToIsoDateOnly,
} from '@/utils/dateFormatting';
import { filterAppointmentsForIsoDate } from '../utils/appointmentFiltering';
import {
  isAppointmentCancelled,
  resolvePatientDisplayName,
} from '../utils/appointmentDisplay';
import { RescheduleRequestMarker } from '../RescheduleRequestMarker';
import { resolveAppointmentColorClasses } from '@/constants/appointmentColors';
import type { MonthViewProps } from '../types';

const WEEK_STARTS_ON_MONDAY = { weekStartsOn: 1 } as const;
const MAXIMUM_VISIBLE_APPOINTMENTS_PER_DAY = 3;

export function MonthView({
  currentDate,
  selectedDate,
  appointments,
  onDaySelected,
}: MonthViewProps) {
  const { t } = useTranslation();
  const buildAppointmentTargetRef = useAssistantTargetCollection(
    ASSISTANT_TARGET_KEY.dashboardAppointmentRow
  );

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const gridStart = startOfWeek(monthStart, WEEK_STARTS_ON_MONDAY);
  const gridEnd = endOfWeek(monthEnd, WEEK_STARTS_ON_MONDAY);
  const calendarDays = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekdayLabels = buildLocalizedWeekdayShortLabels();
  const patientFallbackText = t('appointments:patientFallback');

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-7 border-b border-zooborder bg-zoovpalelavender bg-opacity-30">
        {weekdayLabels.map(function renderWeekdayLabel(weekdayLabel) {
          return (
            <div
              key={weekdayLabel}
              className="py-3 text-center text-xs font-bold text-zoomuted uppercase tracking-wider"
            >
              {weekdayLabel}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-7 flex-1">
        {calendarDays.map(function renderCalendarDay(day) {
          const dayIsoDate = formatDateToIsoDateOnly(day);
          const dayAppointments = filterAppointmentsForIsoDate(appointments, dayIsoDate);
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isToday = isSameDay(day, new Date());
          const isSelected = isSameDay(day, selectedDate);
          const hiddenAppointmentsCount =
            dayAppointments.length - MAXIMUM_VISIBLE_APPOINTMENTS_PER_DAY;

          return (
            <button
              key={dayIsoDate}
              type="button"
              aria-label={t('calendar:dayCellAriaLabel', {
                date: formatDateToFullWeekdayDate(day),
                count: dayAppointments.length,
              })}
              onClick={function selectThisDay() {
                onDaySelected(day);
              }}
              className={cn(
                'text-left min-h-[64px] sm:min-h-[120px] p-1 sm:p-2 border-r border-b border-zooborder cursor-pointer transition-colors hover:bg-zoovpalelavender hover:bg-opacity-20',
                isCurrentMonth === false
                  ? 'bg-zoovpalelavender bg-opacity-10'
                  : isSelected
                    ? 'bg-zoopalelavender bg-opacity-30'
                    : 'bg-zoowhite',
                isSelected ? 'ring-1 ring-inset ring-zooprimary' : ''
              )}
            >
              <div className="flex justify-between items-start mb-1">
                <span
                  className={cn(
                    'text-sm font-bold w-7 h-7 flex items-center justify-center rounded-full',
                    isToday && isSelected
                      ? 'bg-zooprimary text-zoowhite shadow-sm ring-2 ring-offset-1 ring-zooprimary'
                      : isToday
                        ? 'bg-zoovpalelavender text-zooprimary ring-1 ring-zooprimary'
                        : isSelected
                          ? 'bg-zooprimary text-zoowhite shadow-sm'
                          : isCurrentMonth
                            ? 'text-zooindigo'
                            : 'text-zoomuted'
                  )}
                >
                  {formatDateToDayOfMonthNumber(day)}
                </span>
              </div>
              <div className="space-y-1 overflow-hidden mt-1">
                {dayAppointments
                  .slice(0, MAXIMUM_VISIBLE_APPOINTMENTS_PER_DAY)
                  .map(function renderAppointmentChip(appointment) {
                    const colorClasses = resolveAppointmentColorClasses(appointment.therapyType);
                    return (
                      <div
                        key={appointment.id}
                        ref={buildAppointmentTargetRef(appointment.id)}
                        className={cn(
                          'flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] text-zooindigo leading-tight truncate',
                          colorClasses.softBg,
                          colorClasses.accentBorder,
                          isAppointmentCancelled(appointment) ? 'opacity-50 line-through' : ''
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full shrink-0',
                            colorClasses.accentDot
                          )}
                        />
                        <span className="font-bold shrink-0">{appointment.startTime}</span>
                        <span className="truncate min-w-0 [@container(max-width:40rem)]:hidden">
                          {resolvePatientDisplayName(appointment, patientFallbackText)}
                        </span>
                        <RescheduleRequestMarker appointment={appointment} />
                      </div>
                    );
                  })}
                {hiddenAppointmentsCount > 0 && (
                  <div className="text-[10px] font-bold text-zooprimary pl-1">
                    {t('calendar:moreCount', { count: hiddenAppointmentsCount })}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
